import { BASE_APPLICANTDeclined_answer_race_gender } from './personal_loan.model';
import { BASE_CONTACT_INFOPreferred_contact_method, BASE_CUSTOM_QUESTIONSCUSTOM_QUESTION, BASE_MONTHLY_DEBT, base_applicant_type } from './common.model';
import { base_phone_country_alpha2, base_employee_of_lender_types, BASE_STRUCTURED_COMMENTS, BASE_STRUCTURED_COMMENT, BASE_HMDA_XML, BASE_HMDA_XMLITEM } from './xpress_loan.model';
import { HomeEquityData, InterviewMethod, TitleHoldType, MortgageLoanInfoRateType } from './mortgage_loan.model';

// Re-export HMDA types for convenience
export { BASE_HMDA_XML, BASE_HMDA_XMLITEM };

// Demographic data interface for HE applications (UI binding - will be transformed to HMDA_XML before submission)
export interface HEDemographicData {
    sex: { [key: string]: boolean };
    ethnicity: { [key: string]: boolean };
    ethnicityText: { [key: string]: string };
    race: { [key: string]: boolean };
    raceText: { [key: string]: string }
}

// HE Loan Applicant
export class HE_APPLICANT {
    public is_declined: string;
    public first_name: string;
    public last_name: string;
    public m_initial: string;
    public middle_name: string;
    public ssn: string;
    public dob: string;
    public ages_of_dependents: string;
    public mother_maiden_name: string;
    public citizenship: string;
    public member_number: string;
    public membership_months: string;
    public gender: string;
    public ethnicity: string;
    public race: string[];
    public declined_answer_race_gender: string;
    public number_in_household: string;
    public custom_score: string;
    public custom_score_2: string;
    public applicant_type: base_applicant_type;
    public allow_direct_mail: string;
    public allow_email_solicit: string;
    public allow_telemarket: string;
    public allow_share_info: string;
    public is_ofac_id_ver: string;
    public id_authentication_result: string;
    public is_fraud_prescore: string;
    public is_fraud_fas: string;
    public is_fraud_credit: string;
    public is_ofac_credit: string;
    public combined_unsecured_aggregate_amount_with_lender: number;
    public borrower_id: string;
    public CONTACT_INFO: any;
    public CURRENT_ADDRESS: any;
    public MAILING_ADDRESS: any;
    public FINANCIAL_INFO: any;
    public DECLARATION: HEDeclaration;
    public PRESENT_HOUSING_EXPENSE: HEPresentHousingExpense;

    // Joint applicant (SPOUSE) - matches pattern from other loan types
    public SPOUSE?: HE_APPLICANT;

    // Demographic data for UI binding (will be transformed to HMDA_XML before submission)
    public DEMOGRAPHIC_DATA?: HEDemographicData;
    public interview_method : InterviewMethod =InterviewMethod.INTERNET ;
}

// HE Loan Applicants container
export class HE_LOANAPPLICANTS {
    public APPLICANT: HE_APPLICANT[];
    public selected_product_borrower_id: string;
}

// HE Loan Info
export class HE_LOAN_INFO {
    public is_home_equity: string; // Y/N
    public loan_purpose: string;
    public term_months: number;
    public term_monthsSpecified: boolean;
    public estimated_property_value: number;
    public estimated_property_valueSpecified: boolean;
    public current_mortgage_balance: number;
    public current_mortgage_balanceSpecified: boolean;
    public amount_requested: number;
    public amount_requestedSpecified: boolean;
    public loan_term: string;
    public loan_term_requested: string;
    public amount_approved: number;
    public amount_approvedSpecified: boolean;
    public rate: number;
    public rateSpecified: boolean;
    public monthly_payment: number;
    public monthly_paymentSpecified: boolean;
    public purpose_description: string;
    public is_LOC: BASE_APPLICANTDeclined_answer_race_gender;
    public is_LOCSpecified: boolean;
    public apr: number;
    public aprSpecified: boolean;
    public number_of_payments: number;
    public number_of_paymentsSpecified: boolean;
    public rate_code: string;
    public max_underwrite_amount: string;
    public epl_fees_total: number;
    public is_claimed: BASE_APPLICANTDeclined_answer_race_gender;
    public is_complete_consumer: BASE_APPLICANTDeclined_answer_race_gender;
    public require_product_refresh: BASE_APPLICANTDeclined_answer_race_gender;
    public is_addon: BASE_APPLICANTDeclined_answer_race_gender;
    public is_amendment: BASE_APPLICANTDeclined_answer_race_gender;
    public solve_for: string;
    public is_possible_dupe: BASE_APPLICANTDeclined_answer_race_gender;
    public is_workout_loan: BASE_APPLICANTDeclined_answer_race_gender;
    public is_solvefor_manual: BASE_APPLICANTDeclined_answer_race_gender;
    public tier: string;
    public tier_previous: string;
    public is_prequalification_applied: BASE_APPLICANTDeclined_answer_race_gender;
    public is_ofac: BASE_APPLICANTDeclined_answer_race_gender;
    public occupancy_status: string;
    public refinance_purpose: string;
    public HOME_EQUITY_DATA: HomeEquityData;
    public rate_type: MortgageLoanInfoRateType;
}

// HE Property Info (required for HE loans)
export interface HE_PROPERTY_INFO {
    occupancy_status?: string;      // PRIMARY RESIDENCE, SECOND HOME, INVESTMENT
    property_type?: string;         // SFR, CONDO, etc.
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    county?: string;
    building_status?: string;       // EXISTING, PROPOSED, etc.
    occupancy_duration?: number;
}

// HE Loan main class
export class HE_LOAN {
    public APPLICANTS: HE_LOANAPPLICANTS;
    public LOAN_INFO: HE_LOAN_INFO;
    public PROPERTY_INFO: HE_PROPERTY_INFO;
    public SYSTEM: any[];
    public version: number;
    public STRUCTURED_COMMENTS: BASE_STRUCTURED_COMMENTS;
    public CUSTOM_QUESTIONS: BASE_CUSTOM_QUESTIONSCUSTOM_QUESTION[];
    // HMDA XML for demographic data submission (at loan level per backend model)
    public HMDA_XML?: BASE_HMDA_XML;
}

// Interface for API response
export interface HEAllResponse {
    termMonths: HETermMonth[];
    reasons: HEReason[];
    occupancyStatuses: HEOccupancyStatus[];
    interviewMethods: HEInterviewMethod[];
    loanPurposes: HELoanPurpose[];
}

export interface HETermMonth {
    id: number;
    value: number;
    displayName: string;
    isActive: boolean;
}

export interface HEReason {
    id: number;
    value: string;
    displayName: string;
    isActive: boolean;
}

export interface HEOccupancyStatus {
    id: number;
    value: string;
    displayName: string;
    isActive: boolean;
}

export interface HEInterviewMethod {
    id: number;
    value: string;
    displayName: string;
    isActive: boolean;
}

export interface HELoanPurpose {
    id: number;
    value: string;
    displayName: string;
    isActive: boolean;
}

// Present Housing Expense for HE applicant (aligned with XSD PRESENT_HOUSING_EXPENSE)
export interface HEPresentHousingExpense {
    monthly_payment?: number;       // Monthly mortgage/rent payment
    other_pi?: number;              // Other P&I
    hazard_insurance?: number;      // Homeowner's/Hazard Insurance
    real_estate_tax?: number;       // Real Estate Taxes
    mortgage_insurance?: number;    // Mortgage Insurance
    hoa_due?: number;               // HOA Dues
    other_expense?: number;         // Other Expense
}

// HE Declaration section for borrower declarations (aligned with XSD field names)
export interface HEDeclaration {
    // a. Outstanding judgments
    has_outstanding_judgement: string;  // Y/N
    has_outstanding_judgement_explanation?: string;
    // b. Declared bankruptcy within past 7 years
    has_bankruptcy: string;  // Y/N
    has_bankruptcy_explanation?: string;
    // c. Property foreclosed or deed-in-lieu in past 7 years
    has_bad_loan: string;  // Y/N
    has_bad_loan_explanation?: string;
    // d. Party to lawsuit
    is_in_lawsuit: string;  // Y/N
    is_in_lawsuit_explanation?: string;
    // e. Obligated on any loan with foreclosure/judgment/default
    is_related_to_bad_loan: string;  // Y/N
    is_related_to_bad_loan_explanation?: string;
    // f. Delinquent on any Federal debt
    is_presently_delinquent: string;  // Y/N
    is_presently_delinquent_explanation?: string;
    // g. Obligated to pay alimony, child support, or separate maintenance
    has_separate_maintenance: string;  // Y/N
    has_separate_maintenance_explanation?: string;
    // h. Borrowing any part of down payment
    is_down_payment_borrowed: string;  // Y/N
    is_down_payment_borrowed_explanation?: string;
    // i. Co-maker or endorser on a note
    is_endorser_on_note: string;  // Y/N
    is_endorser_on_note_explanation?: string;
    // j. Intend to occupy property as primary residence
    is_property_primary_residence: string;  // Y/N
    // k. Ownership interest in property in last 3 years
    has_owner_ship_interest: string;  // Y/N
    type_of_property_owned?: string;  // PR, SH, IP
}
